import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  online: { label: 'Çevrimiçi', color: 'bg-emerald-500' },
  idle: { label: 'Boşta', color: 'bg-amber-400' },
  dnd: { label: 'Rahatsız Etme', color: 'bg-rose-500' },
  offline: { label: 'Çevrimdışı', color: 'bg-zinc-500' },
};

const DiscordIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M20.3 4.37A19.8 19.8 0 0 0 15.5 3l-.24.5a18.3 18.3 0 0 1 4.3 1.4c-5-2.2-11-2.2-16.1 0A18.3 18.3 0 0 1 7.7 3.5L7.5 3a19.8 19.8 0 0 0-4.9 1.37C.4 8.77-.25 13.06.1 17.3a20 20 0 0 0 6 3c.5-.7.9-1.4 1.3-2.1a13 13 0 0 1-2-.96c.17-.12.34-.25.5-.38 3.9 1.84 8.1 1.84 12 0 .17.13.34.26.5.38a13 13 0 0 1-2 .96c.4.7.8 1.4 1.3 2.1a20 20 0 0 0 6-3c.4-4.94-.7-9.2-3.4-12.93ZM8.5 14.9c-1.2 0-2.2-1.1-2.2-2.4 0-1.3 1-2.4 2.2-2.4 1.2 0 2.2 1.1 2.2 2.4 0 1.3-1 2.4-2.2 2.4Zm7 0c-1.2 0-2.2-1.1-2.2-2.4 0-1.3 1-2.4 2.2-2.4 1.2 0 2.2 1.1 2.2 2.4 0 1.3-1 2.4-2.2 2.4Z"/>
  </svg>
);

export default function DiscordCTA() {
  const [live, setLive] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    const load = () => axios.get(`${API}/discord/live`).then(r => setLive(r.data)).catch(() => setErr('widget kapalı'));
    load();
    const i = setInterval(load, 20000);
    return () => clearInterval(i);
  }, []);

  return (
    <section className="pb-20 lg:pb-28">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl card-soft">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full hero-blob" />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full hero-blob opacity-60" />
          <div className="relative grid lg:grid-cols-5 gap-8 p-8 lg:p-12">
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2">
                <DiscordIcon className="text-[#5865F2]" />
                <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>Discord Topluluk</span>
              </div>
              <h2 className="mt-3 text-3xl lg:text-4xl font-semibold tracking-tight">
                <span>{live?.name || 'TO Studios'}</span>
              </h2>
              <p className="mt-3" style={{ color: 'var(--text-muted)' }}>Sunucumuza katıl, hiçbir güncellemeyi kaçırma.</p>
              <div className="mt-5 flex items-center gap-3">
                <a href={live?.instant_invite || '#'} target="_blank" rel="noreferrer" className="pill-btn font-semibold" style={{ background: '#5865F2', color: '#fff', borderColor: 'transparent' }}>
                  <DiscordIcon /> <span>Sunucuya Katıl</span> <ExternalLink size={12} />
                </a>
                <div className="text-sm flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /> <strong>{live?.presence_count ?? 0}</strong> <span style={{ color: 'var(--text-muted)' }}>çevrimiçi</span></div>
              </div>
            </div>

            <div className="lg:col-span-3">
              <div className="flex items-center justify-between">
                <div className="text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>Şu an aktif üyeler</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{live?.presence_count ?? 0} üye</div>
              </div>
              {err && (
                <div className="mt-3 text-sm" style={{ color: 'var(--text-muted)' }}>
                  Canlı liste için sunucu Widget'ı etkinleştirilmeli.
                </div>
              )}
              <div className="mt-3 max-h-80 overflow-auto pr-1 flex flex-col gap-1.5">
                {(live?.members || []).map(m => {
                  const st = STATUS[m.status] || STATUS.offline;
                  return (
                    <div key={m.id} className="flex items-center gap-3 p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
                      <div className="relative">
                        <img src={m.avatar_url} alt={m.username} className="w-8 h-8 rounded-full" />
                        <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 ${st.color}`} style={{ borderColor: 'var(--bg-soft)' }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{m.username}</div>
                        <div className="text-[11px] truncate" style={{ color: 'var(--text-muted)' }}>
                          {m.game ? <span className="text-violet-500">🎮 {m.game}</span> : st.label}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {!live?.members?.length && !err && (
                  <div className="py-10 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Yükleniyor...</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
